import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

const QUICK_AMOUNTS = [50, 100, 200, 500, 1000];

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export default function PaymentPopup({
  total,
  subtotal,
  vatAmount,
  vatRate,
  paymentMethod,
  onConfirm,
  onCancel,
  cartItems,
  storeSettings,
  printEnabled,
  isOffline = false,
}) {
  const safeSubtotal = safeNumber(subtotal);
  const safeVatAmount = safeNumber(vatAmount);
  const safeVatRate = safeNumber(vatRate);
  const computedTotalWithVat = safeSubtotal + safeVatAmount;
  const safeTotal = Number.isFinite(Number(total))
    ? Number(total)
    : computedTotalWithVat;
  const shouldShowVat = safeVatRate > 0 && safeVatAmount > 0;

  const [tendered, setTendered] = useState("");
  const [success, setSuccess] = useState(false);
  const [change, setChange] = useState(null);
  const [printAfter, setPrintAfter] = useState(printEnabled !== false);
  const inputRef = useRef(null);
  // Guards onConfirm against firing twice — once from the automatic
  // print-completion/timeout chain, once from the user manually tapping
  // "Back to POS". Only the first caller wins; the RPC in handleCheckout
  // must only ever run once per charge.
  const finishedRef = useRef(false);

  // ── Print ────────────────────────────────────────────────
  // Defined before handleConfirm so useCallback closure can reference it
  // Takes an onDone callback and defers it until the print window is
  // actually finished with printing — calling it too early (e.g. right
  // after win.print()) unmounts this component while Chrome's print
  // pipeline is still mid-flight, throwing "The provided callback is
  // no longer runnable."
  const triggerPrint = useCallback((onDone) => {
    const items = cartItems ?? [];
    const sName = storeSettings?.store_name ?? "Guadaye Mini Mart";
    const sAddress = storeSettings?.store_address ?? "";
    const sPhone = storeSettings?.store_phone ?? "";
    const sTIN = storeSettings?.store_tin ?? "";
    const vatOn = storeSettings?.vat_enabled === "true";

    const win = window.open("", "_blank", "width=320,height=600");
    if (!win) {
      onDone?.();
      return; // popup blocked
    }

    win.document.write(`
            <html><head><title>Receipt</title>
            <style>
              body  { font-family: monospace; font-size: 12px; padding: 16px; max-width: 280px; margin: 0 auto; }
              .center { text-align: center; }
              .bold   { font-weight: bold; }
              .line   { border-top: 1px dashed #000; margin: 8px 0; }
              .row    { display: flex; justify-content: space-between; margin: 2px 0; }
            </style></head><body>
            <div class="center bold" style="font-size:14px">${sName}</div>
            ${sAddress ? `<div class="center">${sAddress}</div>` : ""}
            ${sPhone ? `<div class="center">Tel: ${sPhone}</div>` : ""}
            ${sTIN ? `<div class="center">TIN: ${sTIN}</div>` : ""}
            <div class="line"></div>
            <div class="row"><span>Date:</span><span>${new Date().toLocaleString()}</span></div>
            <div class="line"></div>
            ${items
              .map(
                (i) => `
              <div>${i.name}</div>
              <div class="row">
                <span>${i.qty} x ETB ${Number(i.selling_price).toFixed(2)}</span>
                <span>ETB ${(i.qty * i.selling_price).toFixed(2)}</span>
              </div>
            `,
              )
              .join("")}
            <div class="line"></div>
            <div class="row"><span>Subtotal</span><span>ETB ${safeSubtotal.toFixed(2)}</span></div>
            ${
              vatOn && safeVatAmount > 0
                ? `<div class="row"><span>VAT (${(safeVatRate * 100).toFixed(0)}%)</span><span>ETB ${safeVatAmount.toFixed(2)}</span></div>`
                : ""
            }
            <div class="row bold"><span>TOTAL</span><span>ETB ${safeTotal.toFixed(2)}</span></div>
            ${
              paymentMethod === "cash"
                ? `
              <div class="row"><span>Tendered</span><span>ETB ${safeNumber(tendered).toFixed(2)}</span></div>
              <div class="row"><span>Change</span><span>ETB ${Math.max(0, safeNumber(tendered) - safeTotal).toFixed(2)}</span></div>
            `
                : `<div class="row"><span>Payment</span><span style="text-transform:capitalize">${paymentMethod}</span></div>`
            }
            <div class="line"></div>
            <div class="center">Thank you for shopping!</div>
            <div class="center" style="margin-top:4px;font-size:10px">${sName}</div>
            </body></html>
        `);
    win.document.close();
    win.print();

    // Wait for the print window to signal it's done (dialog dismissed or
    // printed) before handing control back — fall back to a short delay
    // if 'afterprint' never fires for some reason.
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      onDone?.();
    };
    win.addEventListener("afterprint", finish);
    setTimeout(finish, 500);
  }, [
    cartItems,
    storeSettings,
    safeSubtotal,
    safeVatAmount,
    safeVatRate,
    safeTotal,
    paymentMethod,
    tendered,
  ]);

  // ── Confirm ──────────────────────────────────────────────
  // Single-fire gate: whichever path reaches here first (the automatic
  // print-completion/timeout chain, or the user manually tapping "Back to
  // POS") wins; every later caller is a no-op. Keeps onConfirm — and the
  // handleCheckout/process_sale RPC it triggers — to exactly one call.
  const finishOnce = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onConfirm(paymentMethod === "cash" ? safeNumber(tendered) : safeTotal);
  }, [onConfirm, paymentMethod, tendered, safeTotal]);

  const handleConfirm = useCallback(() => {
    if (paymentMethod === "cash") {
      if (!tendered || safeNumber(tendered) < safeTotal) {
        alert(`Amount tendered must be at least ETB ${safeTotal.toFixed(2)}`);
        return;
      }
    }
    setSuccess(true);
    setTimeout(() => {
      if (printAfter) {
        triggerPrint(finishOnce);
      } else {
        finishOnce();
      }
    }, 800);
  }, [paymentMethod, tendered, safeTotal, printAfter, triggerPrint, finishOnce]);

  // ── Effects ──────────────────────────────────────────────
  useEffect(() => {
    if (paymentMethod === "cash") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [paymentMethod]);

  useEffect(() => {
    if (tendered === "") {
      setChange(null);
      return;
    }
    setChange(safeNumber(tendered) - safeTotal);
  }, [tendered, safeTotal]);

  useEffect(() => {
    function onDocKeyDown(e) {
      if (e.key === "Enter") handleConfirm();
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onDocKeyDown);
    return () => document.removeEventListener("keydown", onDocKeyDown);
  }, [handleConfirm, onCancel]);

  function handleQuickAmount(amount) {
    setTendered(amount.toString());
    inputRef.current?.focus();
  }

  // ── Success screen ───────────────────────────────────────
  if (success) {
    const changeAmt =
      paymentMethod === "cash" ? safeNumber(tendered) - safeTotal : 0;
    return (
      <div className="payment-popup-overlay">
        <div className="success-popup">
          <div className="success-icon">✓</div>
          <p className="success-title">
            {isOffline ? "Sale Queued!" : "Sale Complete!"}
          </p>
          <p
            className="success-receipt"
            style={{
              color: isOffline ? "var(--amber)" : "var(--green)",
              fontSize: "13px",
            }}
          >
            {isOffline
              ? "📵 Saved offline · will sync when connected"
              : printAfter
                ? "🖨 Printing receipt..."
                : "✓ Done"}
          </p>
          <div className="success-details">
            <div className="success-row">
              <span>Total</span>
              <strong>ETB {safeTotal.toFixed(2)}</strong>
            </div>
            {paymentMethod === "cash" && (
              <>
                <div className="success-row">
                  <span>Tendered</span>
                  <strong>ETB {safeNumber(tendered).toFixed(2)}</strong>
                </div>
                <div className="success-row change">
                  <span>Change</span>
                  <strong>ETB {Math.max(0, changeAmt).toFixed(2)}</strong>
                </div>
              </>
            )}
            <div className="success-row">
              <span>Payment</span>
              <strong style={{ textTransform: "capitalize" }}>
                {paymentMethod}
              </strong>
            </div>
          </div>
          <button className="new-sale-btn" onClick={finishOnce}>
            ← Back to POS
          </button>
        </div>
      </div>
    );
  }

  // ── Main popup ───────────────────────────────────────────
  return (
    <div className="payment-popup-overlay">
      <div className="payment-popup">
        {/* Header */}
        <div style={{ marginBottom: "16px" }}>
          <p className="popup-title">
            {paymentMethod === "cash"
              ? "💵 Cash Payment"
              : paymentMethod === "telebirr"
                ? "📱 Telebirr Payment"
                : "🏦 Bank Transfer"}
          </p>
          <p
            style={{
              fontSize: "12px",
              color: "var(--text3)",
              marginTop: "4px",
            }}
          >
            {paymentMethod === "cash"
              ? "Enter the amount received from customer"
              : "Confirm the payment amount"}
          </p>
        </div>

        {/* Amount breakdown */}
        <div style={{ marginBottom: "16px" }}>
          <p className="popup-label">Amount Breakdown</p>
          <div
            style={{
              background: "var(--elevated)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              padding: "12px",
              fontFamily: "var(--mono)",
              fontSize: "13px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "var(--text2)",
                marginBottom: "6px",
              }}
            >
              <span>Subtotal</span>
              <span>ETB {safeSubtotal.toFixed(2)}</span>
            </div>
            {shouldShowVat && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "var(--amber)",
                  marginBottom: "6px",
                }}
              >
                <span>VAT ({(safeVatRate * 100).toFixed(0)}%)</span>
                <span>ETB {safeVatAmount.toFixed(2)}</span>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: "var(--cyan)",
                fontWeight: 700,
                borderTop: "1px solid var(--border)",
                paddingTop: "8px",
              }}
            >
              <span>TOTAL</span>
              <span>ETB {safeTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Cash inputs */}
        {paymentMethod === "cash" && (
          <>
            {/* Quick amounts */}
            <div style={{ marginBottom: "12px" }}>
              <p className="popup-label" style={{ marginBottom: "8px" }}>
                Quick Amounts
              </p>
              <div className="quick-amounts">
                {QUICK_AMOUNTS.filter(
                  (a) => a >= Math.floor(safeTotal / 100) * 100,
                )
                  .slice(0, 5)
                  .map((amt) => (
                    <button
                      key={amt}
                      className="quick-btn"
                      onClick={() => handleQuickAmount(amt)}
                    >
                      {amt}
                    </button>
                  ))}
                <button
                  className="quick-btn"
                  style={{
                    color: "var(--cyan)",
                    borderColor: "rgba(50,212,224,0.3)",
                  }}
                  onClick={() => handleQuickAmount(Math.ceil(safeTotal))}
                >
                  Exact
                </button>
              </div>
            </div>

            {/* Tendered input */}
            <div style={{ marginBottom: "12px" }}>
              <p className="popup-label" style={{ marginBottom: "8px" }}>
                Amount Tendered (ETB)
              </p>
              <input
                ref={inputRef}
                type="number"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                placeholder={safeTotal.toFixed(2)}
                className="tendered-input"
              />
            </div>

            {/* Change display */}
            {change !== null && (
              <div
                className={`change-display ${change < 0 ? "insufficient" : ""}`}
              >
                <span className="change-label">
                  {change < 0 ? "⚠ Insufficient" : "Change to Return"}
                </span>
                <span
                  className={`change-amount ${change < 0 ? "insufficient" : ""}`}
                >
                  ETB {Math.abs(change).toFixed(2)}
                </span>
              </div>
            )}
          </>
        )}

        {/* Non-cash confirmation */}
        {paymentMethod !== "cash" && (
          <div
            style={{
              padding: "16px",
              background: "var(--elevated)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: "13px", color: "var(--text2)" }}>
              {paymentMethod === "telebirr"
                ? "📱 Ask customer to send ETB " +
                  safeTotal.toFixed(2) +
                  " via Telebirr"
                : "🏦 Ask customer to transfer ETB " +
                  safeTotal.toFixed(2) +
                  " to your bank account"}
            </p>
            <p
              style={{
                fontSize: "11px",
                color: "var(--text3)",
                marginTop: "8px",
              }}
            >
              Confirm once payment is received
            </p>
          </div>
        )}

        {/* Print toggle — only shown when receipt printing is enabled in Settings */}
        {printEnabled && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 0",
              borderTop: "1px solid var(--border)",
              marginBottom: "12px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
                fontSize: "13px",
                color: "var(--text2)",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={printAfter}
                onChange={(e) => setPrintAfter(e.target.checked)}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  accentColor: "var(--cyan)",
                }}
              />
              Print receipt after payment
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="popup-actions">
          <button className="popup-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="popup-confirm"
            onClick={handleConfirm}
            disabled={paymentMethod === "cash" && (!tendered || change < 0)}
          >
            {paymentMethod === "cash"
              ? `Confirm — Change ETB ${change !== null && change >= 0 ? change.toFixed(2) : "0.00"}`
              : "✓ Confirm Payment"}
          </button>
        </div>

        {/* Keyboard hint */}
        <p
          style={{
            textAlign: "center",
            fontSize: "11px",
            color: "var(--text3)",
            marginTop: "10px",
          }}
        >
          Press{" "}
          <kbd
            style={{
              background: "var(--elevated)",
              padding: "1px 6px",
              borderRadius: "4px",
              border: "1px solid var(--border)",
            }}
          >
            Enter
          </kbd>{" "}
          to confirm ·{" "}
          <kbd
            style={{
              background: "var(--elevated)",
              padding: "1px 6px",
              borderRadius: "4px",
              border: "1px solid var(--border)",
            }}
          >
            Esc
          </kbd>{" "}
          to cancel
        </p>
      </div>
    </div>
  );
}
